import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CloseIcon, FlameMark, MenuIcon, SteamIcon } from './icons';
import { STEAM_URL } from '../site-config.mjs';
import { trackWishlistClick } from '../analytics';
import './site-header.css';

const NAV_LINKS = [
  { label: 'The game', to: '/#the-game', section: 'the-game' },
  { label: 'Trailer', to: '/#trailer', section: 'trailer' },
  { label: 'Screens', to: '/#screens', section: 'screens' },
  { label: 'Feedback', to: '/feedback' },
];

export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const { pathname, hash } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Highlight the section the reader is actually in, not the last anchor they clicked. */
  useEffect(() => {
    if (!isHome || typeof IntersectionObserver === 'undefined') {
      setActiveSection(null);
      return undefined;
    }

    const sections = NAV_LINKS.filter((link) => link.section)
      .map((link) => document.getElementById(link.section))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.id);
      },
      { rootMargin: '-45% 0px -45% 0px' },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [isHome]);

  useEffect(() => setIsMenuOpen(false), [pathname, hash]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <header className={`site-header${isScrolled || isMenuOpen ? ' is-lit' : ''}`}>
      <div className="site-header__bar shell">
        <Link className="site-header__brand" to="/" aria-label="Warm home">
          <FlameMark size={24} />
          <span className="site-header__brand-name">Warm</span>
          <span className="site-header__brand-studio">OutHereGaming</span>
        </Link>

        <nav className="site-header__nav" aria-label="Primary">
          {NAV_LINKS.map(({ label, to, section }) => {
            const isActive = section ? isHome && activeSection === section : pathname === to;
            return (
              <Link
                key={to}
                className={`site-header__link${isActive ? ' is-active' : ''}`}
                to={to}
                aria-current={isActive ? 'page' : undefined}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="site-header__actions">
          <a
            className="btn btn--primary site-header__steam"
            href={STEAM_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWishlistClick('header')}
          >
            <SteamIcon />
            Play on Steam
          </a>
          <button
            type="button"
            className="site-header__toggle"
            aria-expanded={isMenuOpen}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <nav className="site-header__drawer" aria-label="Mobile">
          {NAV_LINKS.map(({ label, to }) => (
            <Link key={to} className="site-header__drawer-link" to={to}>
              {label}
            </Link>
          ))}
          <a
            className="btn btn--primary btn--lg"
            href={STEAM_URL}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWishlistClick('mobile-menu')}
          >
            <SteamIcon />
            Play on Steam
          </a>
        </nav>
      )}
    </header>
  );
}
